import { Link } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import type { StockMovement, MovementType } from '../../types';

const movementTypeConfig: Record<
  MovementType,
  { label: string; className: string }
> = {
  receive: { label: 'Received', className: 'bg-green-500 text-white' },
  transfer: { label: 'Transfer', className: 'bg-blue-500 text-white' },
  pick: { label: 'Picked', className: 'bg-purple-500 text-white' },
  ship: { label: 'Shipped', className: 'bg-cyan-500 text-white' },
  adjust: { label: 'Adjusted', className: 'bg-yellow-500 text-black' },
  return: { label: 'Returned', className: 'bg-orange-500 text-white' },
  scrap: { label: 'Scrapped', className: 'bg-red-500 text-white' },
  production_complete: { label: 'Production', className: 'bg-teal-500 text-white' },
  status_change: { label: 'Status', className: 'bg-violet-500 text-white' },
};

interface RecentMovementsProps {
  movements: StockMovement[];
  isLoading?: boolean;
}

export function RecentMovements({ movements, isLoading }: RecentMovementsProps) {
  if (isLoading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Recent Activity</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            {[...Array(5)].map((_, i) => (
              <div key={i} className="h-12 bg-muted rounded animate-pulse" />
            ))}
          </div>
        </CardContent>
      </Card>
    );
  }

  if (movements.length === 0) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Recent Activity</CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-muted-foreground text-center py-4">
            No recent movements. Activity will appear here as stock is managed.
          </p>
        </CardContent>
      </Card>
    );
  }

  const formatTime = (dateStr: string) => {
    const date = new Date(dateStr);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    return date.toLocaleDateString();
  };

  return (
    <Card>
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between">
          <CardTitle className="text-base">Recent Activity</CardTitle>
          <Button variant="ghost" size="sm" asChild>
            <Link to="/wms/movements">View All</Link>
          </Button>
        </div>
      </CardHeader>
      <CardContent>
        <div className="space-y-3">
          {movements.slice(0, 8).map((movement) => {
            const config = movementTypeConfig[movement.movement_type];
            return (
              <div
                key={movement.id}
                className="flex items-center justify-between py-2 border-b border-muted last:border-0"
              >
                <div className="flex items-center gap-3 min-w-0">
                  <Badge className={config.className}>{config.label}</Badge>
                  <div className="min-w-0">
                    <p className="text-sm font-mono truncate">{movement.sku_code}</p>
                    <p className="text-xs text-muted-foreground">
                      {movement.performed_by_name || 'System'}
                    </p>
                  </div>
                </div>
                <div className="text-right ml-3">
                  <p className="text-sm font-medium">
                    {movement.quantity > 0 ? '+' : ''}
                    {movement.quantity}
                  </p>
                  <p className="text-xs text-muted-foreground">
                    {formatTime(movement.performed_at)}
                  </p>
                </div>
              </div>
            );
          })}
        </div>
      </CardContent>
    </Card>
  );
}
